import React, { useEffect, useState } from "react";
import classes from "../style.module.scss";
import moment from "moment";
import { useHistory, useLocation, Redirect } from "react-router";
import { isEmpty } from "lodash";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";

import DashboardLayout from "..";
import DashboardTable from "components/Dashboard/Table";
import { CreateCommunityDialog, CreateFirstCommunity } from "components/Dashboard/Actions";
import Loading from "components/Loading";
import Button from "components/Button";

import { CHBA_RENO_COMM_ID } from "utils/communities/api";
import { getRegion } from "utils/communities";
import { getCommunitySortComparator } from "utils/directory/sorting";

import { selectUserCommDir, selectUserCommDirLoading, selectUserError, selectUserUid } from "store/users/selectors";
import { actions as userActions } from "store/users";
import { selectCommunityLoading } from "store/community/selectors";
import { createCommunity, fetchCommunity } from "store/community/thunk";
import { cleanUpCommunity } from "store/community/actions";

import AddIcon from "assets/images/icons/JSX/Add";
import HeaderImg from "assets/images/communitiesHeader.svg";

const { fetchUserCommDir } = userActions;

const CommunitiesDashboard = ({
    uid,
    error,
    fetchUserCommDir,
    commDir,
    commDirLoading,
    communityLoading,
    createCommunity,
}) => {
    const history = useHistory();
    const { pathname } = useLocation();
    const path = pathname.split("/").filter((str) => ["", "dashboard", "communities"].includes(str) === false) || [];
    const currentFolderId = path[path.length - 1] || null;

    /* Loading state and cursor position for loading spinner */
    const [cursorPosition, setCursorPosition] = useState({ top: 0, left: 0 });
    const [isLoading, setIsLoading] = useState(true);

    /* Creating a new Community */
    const [toCreateComm, setToCreateComm] = useState(false);
    const [communityName, setCommunityName] = useState("");

    /* Fetch user's community directory data */
    useEffect(() => {
        if (uid) {
            fetchUserCommDir(uid, false);
        }
    }, [uid]);

    const [allFolders, setAllFolders] = useState([]);
    const [allCommunities, setAllCommunities] = useState([]);
    const [filteredFoldersCommunities, setFilteredFoldersCommunities] = useState([]);
    const [sortBy, setSortBy] = useState("name");
    const [sortOrder, setSortOrder] = useState("asc");

    /* Update loading state once directory has loaded */
    useEffect(() => {
        if (!isEmpty(commDir) && !communityLoading) {
            setIsLoading(false);
        }
    }, [commDir]);

    useEffect(() => {
        const { communities: singleCommunities = {}, folders = {} } = commDir;

        var folderCounts = {};
        const folderRegionLists = {};
        Object.entries({ ...singleCommunities, ...folders }).forEach(([key, { parentFolderId, region }]) => {
            if (parentFolderId) {
                if (parentFolderId in folderCounts) folderCounts[parentFolderId] += 1;
                else folderCounts[parentFolderId] = 1;
            }
            if (parentFolderId && region) {
                if (parentFolderId in folderRegionLists) folderRegionLists[parentFolderId].push(region);
                else folderRegionLists[parentFolderId] = [region];
            }
        });

        // TODO: Displays only CHBA for now (Remove when pushing Custom Communities)
        // const filteredFolders = [];
        // const filteredCommunities = !isEmpty(singleCommunities)
        //     ? Object.entries(singleCommunities)
        //         .filter(([key, value]) => key === CHBA_RENO_COMM_ID)
        //         .map(([key, el]) => [key, { ...el, type: "community" }])
        //     : [];

        /* TODO: Uncomment when pushing Custom Communities */
        const getFolderRegion = (subRegions = []) => {
            var provinceList = [];
            subRegions.forEach((label) => provinceList.push(...label.split(", ")));
            return !provinceList.includes("CAN") ? getRegion(provinceList) : "CAN";
        };

        const filteredFolders = !isEmpty(folders)
            ? Object.entries(folders)
                .filter(([key, { parentFolderId }]) => parentFolderId === currentFolderId)
                .map(([key, el]) => [key, {
                    ...el,
                    size: folderCounts[key] || 0,
                    region: getFolderRegion(folderRegionLists[key]),
                    type: "folder"
                }])
                .sort(getCommunitySortComparator(sortBy, sortOrder))
            : [];

        const filteredCommunities = !isEmpty(singleCommunities)
            ? Object.entries(singleCommunities)
                .filter(([key, { parentFolderId }]) =>
                    parentFolderId ? parentFolderId === currentFolderId : currentFolderId ? false : true
                )
                .map(([key, el]) => [key, { ...el, type: "community" }])
                .sort(getCommunitySortComparator(sortBy, sortOrder))
                .sort(([aKey, aValues], [bKey, bValues]) => {
                    if (aKey === CHBA_RENO_COMM_ID)
                        return -1;
                    else if (bKey === CHBA_RENO_COMM_ID)
                        return 1;
                })
            : [];
        /* End of Custom Communities */

        setAllFolders(folders);
        setAllCommunities(singleCommunities);
        setFilteredFoldersCommunities([...filteredFolders, ...filteredCommunities]);
    }, [commDir, currentFolderId, sortBy, sortOrder]);

    if (!isLoading && path.length > 0 && !allFolders[path[0]]) {
        return <Redirect to="/404-page-not-found" />;
    }

    return (
        <DashboardLayout
            isLoading={isLoading}
            cursorPosition={cursorPosition}
            setCursorPosition={setCursorPosition}
            folders={allFolders}
            dirLoading={commDirLoading}
            withHeader
            headerGradient={"linear-gradient(90deg, #18C1AD 0%, #1B97BA 100%)"}
            headerImg={HeaderImg}
            headerButtonConainerContent={
                <>
                    <h3>Communities</h3>
                    <Button
                        icon={AddIcon}
                        type="white"
                        large
                        onClick={() => setToCreateComm(true)}
                        style={{ width: "fit-content" }}
                    >
                        Create New Community
                    </Button>
                </>
            }
        >
            {/* Loading background */}
            {commDirLoading && <Loading className={classes.loading} message="Loading Communities" />}

            {/* Display table header and communities or folders in current folder */}
            {!commDirLoading && (
                <div className={classes.dashboardBody}>
                    {error && <p>{error}</p>}

                    <DashboardTable
                        history={history}
                        hasNestedPaths
                        rootDir={"Community Directory"}
                        rootPath={`/communities/dashboard`}
                        folders={allFolders}
                        draggable
                        allowMultiSelect
                        setCursorPosition={setCursorPosition}
                        fetchDir={fetchUserCommDir}
                        dirType={"commDir"}
                        allFoldersItems={filteredFoldersCommunities}
                        itemType={"community"}
                        setIsLoading={setIsLoading}
                        sortData={{ sortBy, setSortBy, sortOrder, setSortOrder }}
                    />
                </div>
            )}

            {/* Display for empty folders or empty commDirs */}
            {!commDirLoading && isEmpty(filteredFoldersCommunities) && (
                <CreateFirstCommunity
                    currentFolderId={currentFolderId}
                    communityLoading={communityLoading}
                    createCommunity={createCommunity}
                    uid={uid}
                />
            )}

            {/* Create Community Dialog */}
            {toCreateComm && (
                <CreateCommunityDialog
                    openDialog={toCreateComm}
                    newCommunityName={communityName}
                    setNewCommunityName={(newVal) => setCommunityName(newVal)}
                    onClick={async () => {
                        await createCommunity(uid, communityName, currentFolderId);
                    }}
                    onClose={() => setToCreateComm(false)}
                    communityLoading={communityLoading}
                />
            )}
        </DashboardLayout>
    );
};

const mapStateToProps = createStructuredSelector({
    uid: selectUserUid,
    error: selectUserError,
    commDir: selectUserCommDir,
    commDirLoading: selectUserCommDirLoading,
    communityLoading: selectCommunityLoading,
});

const mapDispatchToProps = (dispatch, { history, uid }) => ({
    fetchUserCommDir: (uid, skipLoading) => dispatch(fetchUserCommDir(uid, skipLoading)),

    createCommunity: async (uid, newCommunityName, parentFolderId = null) => {
        const communityId = await dispatch(
            createCommunity(
                {
                    name: newCommunityName,
                    totalModels: 0,
                    parentFolderId,
                    createdAt: moment().format(),
                    sharedWith: [uid],
                    regions: [],
                    weatherLocation: {},
                },
                uid,
                parentFolderId
            )
        ).then((id) => id);

        await Promise.all([
            dispatch(cleanUpCommunity()),
            dispatch(fetchCommunity(communityId))
        ]).then(() => history.push(`/communities/${communityId}`));
    },
});

export default connect(mapStateToProps, mapDispatchToProps)(CommunitiesDashboard);
